/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Card, Spin, Tag } from "antd";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import {
  DeviceViewDataByServerInterface,
  DeviceViewDataByBankInterface,
} from "../../../interfaces/Monitoring.interface";
// COMPONENTS
import MonitoringDeviceLift from "./MonitoringDeviceLift";
// INTERFACES
import { RootState } from "../../../app/store";

export default function MonitoringDeviceCard({
  dataByServer,
  dataByBank,
  itemsPerColumn,
}: {
  dataByServer: DeviceViewDataByServerInterface[];
  dataByBank: DeviceViewDataByBankInterface[];
  itemsPerColumn: number;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [localBanks, setLocalBanks] = useState<DeviceViewDataByBankInterface[]>([]);
  const [localServers, setLocalServers] = useState<DeviceViewDataByServerInterface[]>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { monitoringDeviceConfiguration } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  function chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  const buildChunks = () => {
    // BY BANK
    setLocalBanks(
      dataByBank.map((item: DeviceViewDataByBankInterface) => ({
        ...item,
        liftsChunks: chunkArray(item.lifts, itemsPerColumn),
      })),
    );
    // BY SERVER
    setLocalServers(
      dataByServer.map((item: DeviceViewDataByServerInterface) => ({
        ...item,
        liftsChunks: chunkArray(item.lifts, itemsPerColumn),
      })),
    );
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [monitoringDeviceConfiguration]);

  useEffect(() => {
    buildChunks();
  }, [dataByServer, dataByBank]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={isLoading}>
      <div className="generalStyles__flexWrap mt-3">
        {monitoringDeviceConfiguration.viewType === "server" ? (
          <>
            {/** BY SERVER */}
            {localServers.map((item: DeviceViewDataByServerInterface) => (
              <Card
                key={item.idServer}
                loading={isLoading}
                title={
                  <div className="generalStyles__textCenter generalStyles__width100">
                    Server / {item.nameServer}
                  </div>
                }
                bordered
                bodyStyle={{ padding: 0, minHeight: 100, paddingBottom: 15 }}
                style={{ marginRight: "15px", marginTop: "15px" }}
              >
                <div className="generalStyles__flex">
                  {item.liftsChunks.map(
                    (chunk: { id: string; name: string; bank: string }[], chunkIndex: number) => (
                      <div
                        key={chunkIndex}
                        style={{
                          borderRight: `${
                            chunkIndex + 1 < item.liftsChunks.length ? "1px" : "0px"
                          } solid #303030`,
                        }}
                      >
                        <div
                          className="generalStyles__flex generalStyles__textCenter mt-3"
                          style={{ height: "30px" }}
                        >
                          <div
                            className="generalStyles__info"
                            style={{
                              width: "200px",
                              fontWeight: "bolder",
                              fontSize: "18px",
                            }}
                          >
                            Unit Name
                          </div>
                          <div className="generalStyles__mrFix" style={{ width: "100px" }}>
                            <Tag style={{ width: "100px", textAlign: "center" }} color="#108ee9">
                              Previous State
                            </Tag>
                          </div>
                          <div className="generalStyles__mrFix" style={{ width: "100px" }}>
                            <Tag style={{ width: "100px", textAlign: "center" }} color="#87d068">
                              Current State
                            </Tag>
                          </div>
                        </div>
                        {chunk.map((liftChunk: { id: string; name: string; bank: string }) => (
                          <div key={liftChunk.id} className="generalStyles__flex">
                            <MonitoringDeviceLift liftChunk={liftChunk} />
                          </div>
                        ))}
                      </div>
                    ),
                  )}
                </div>
              </Card>
            ))}
          </>
        ) : (
          <>
            {/** BY BANK */}
            {localBanks.map((item: DeviceViewDataByBankInterface) => (
              <Card
                key={item.idBank}
                loading={isLoading}
                title={
                  <div className="generalStyles__textCenter generalStyles__width100">
                    Bank / {item.nameBank}
                  </div>
                }
                bordered
                bodyStyle={{ padding: 0, minHeight: 100, paddingBottom: 15 }}
                style={{ marginRight: "10px", marginTop: "10px" }}
              >
                <div className="generalStyles__flex">
                  {item.liftsChunks.map(
                    (chunk: { id: string; name: string }[], chunkIndex: number) => (
                      <div
                        key={chunkIndex}
                        style={{
                          borderRight: `${
                            chunkIndex + 1 < item.liftsChunks.length ? "1px" : "0px"
                          } solid #303030`,
                        }}
                        className="generalStyles__row"
                      >
                        <div
                          className="generalStyles__flex generalStyles__textCenter mt-3"
                          style={{ height: "30px" }}
                        >
                          <div
                            className="generalStyles__info"
                            style={{
                              width: "200px",
                              fontWeight: "bolder",
                              fontSize: "18px",
                            }}
                          >
                            Unit Name
                          </div>
                          <div className="generalStyles__mrFix" style={{ width: "100px" }}>
                            <Tag style={{ width: "100px", textAlign: "center" }} color="#108ee9">
                              Previous State
                            </Tag>
                          </div>
                          <div className="generalStyles__mrFix" style={{ width: "100px" }}>
                            <Tag style={{ width: "100px", textAlign: "center" }} color="#87d068">
                              Current State
                            </Tag>
                          </div>
                        </div>
                        {chunk.map((liftChunk: { id: string; name: string }) => (
                          <div key={liftChunk.id} className="generalStyles__flex">
                            <MonitoringDeviceLift liftChunk={liftChunk} />
                          </div>
                        ))}
                      </div>
                    ),
                  )}
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    </Spin>
  );
}
